import logo from '@/assets/images/fullLogo.png';
import '@/assets/style/sideNav.css';
import { decryptData, encryptData } from '@/components/reusable/crypto/Cipher';
import {
  AimOutlined,
  BookOutlined,
  DashboardOutlined,
  DollarOutlined,
  GoldOutlined,
  MoneyCollectOutlined,
  PieChartOutlined,
  PrinterOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { ConfigProvider, Layout, Menu } from 'antd';
// import Search from 'antd/es/transfer/search';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import SideNavDrawer from './Drawer';

const { Sider } = Layout;

const iconMapping = {
  UserOutlined: <UserOutlined />,
  DashBoardOutlined: <DashboardOutlined />,
  BookOutlined: <BookOutlined />,
  MoneyCollectOutlined: <MoneyCollectOutlined />,
  PieChartOutlined: <PieChartOutlined />,
  GoldOutlined: <GoldOutlined />,
  Accounting: <AimOutlined />,
  Loan: <DollarOutlined />,
  PrinterOutlined: <PrinterOutlined />,
  DefaultIcon: <SettingOutlined />,
};

/**
 * Sidebar component for navigation.
 *
 * @param {Object} props - Component props.
 * @param {boolean} props.collapsed - Indicates if the sidebar is collapsed.
 * @param {string} [props.colorBgContainer] - Background color of the container.
 * @returns {JSX.Element} The rendered sidebar component.
 */
const SideBar = ({ collapsed }) => {
  const [pageTitle, setPageTitle] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);
  const [navigationMenus, setNavigationMenus] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const currentUrl = location.pathname;
  const siderStyle = {
    overflow: 'auto',
    height: '100vh',
    position: 'sticky',
    left: 0,
    top: 0,
    bottom: 0,
  };

  /**
   * Handles navigation to the selected menu item.
   *
   * @param {Object} item - The menu item.
   */
  const toNavigate = (item) => {
    navigate(item.key);
  };

  const data = localStorage.getItem('menuData')
    ? decryptData(localStorage.getItem('menuData'))
    : [];

  /**
   * Handles search query and filters the navigation menus based on the query.
   *
   * @param {string} value - The search query.
   */
  const handleSearch = (value) => {
    setSearchQuery(value);
    if (value) {
      const results = [];
      const searchItems = (menus) => {
        menus.forEach((menu) => {
          if (menu.label.toLowerCase().includes(value.toLowerCase())) {
            results.push({ key: menu.key, label: menu.label, icon: menu.icon });
          }
          if (menu.children) {
            searchItems(menu.children);
          }
        });
      };
      searchItems(navigationMenus);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  /**
   * Transforms the raw menu data to the required format.
   *
   * @param {Array} menuData - The raw menu data.
   * @returns {Array} The transformed menu data.
   */
  // const transformMenuData = (menuData) => {
  //   const transformItem = (item) => {
  //     const isReportsMenu = item.name === 'Reports';

  //     return {
  //       key: isReportsMenu
  //         ? '/reports/'
  //         : item.subMenus
  //           ? item.id
  //           : item.listUrl || item.createUrl || '#',
  //       icon: iconMapping[item.icon] || <span className="anticon" />, // Map the icon string to the icon component
  //       id: item.id,
  //       label: item.name,
  //       children: isReportsMenu
  //         ? null
  //         : item.subMenus
  //           ? item.subMenus?.map(transformItem)
  //           : null,
  //     };
  //   };
  //   return menuData?.map(transformItem);
  // };

  const transformMenuData2 = (menuData) => {
    let reportsSubMenus = null; // Variable to hold "Reports" submenus
    const transformItem = (item) => {
      const isReportsMenu = item.name === 'Reports';

      // If it's "Reports", store its submenus
      if (isReportsMenu) {
        reportsSubMenus = item.subMenus || []; // Save the submenus to variable
      }

      return {
        key: isReportsMenu
          ? '/reports/'
          : item.subMenus
            ? item.id
            : item.listUrl || item.createUrl || '#',
        icon: iconMapping[item?.icon] || <span className="anticon" />,
        id: item.id,
        label: item.name,
        children: isReportsMenu ? null : item.subMenus?.map(transformItem), // Exclude "Reports" submenus
      };
    };

    const transformedData = menuData?.map(transformItem);

    // Save the "Reports" submenus to session storage after transformation
    localStorage.setItem('reportsSubMenus', encryptData(reportsSubMenus));
    return transformedData;
  };

  const transformMenuData = (menuData) => {
    let reportsSubMenus = null;

    const transformItem = (item) => {
      const isReportsMenu = item.name === 'Reports';

      if (isReportsMenu) {
        reportsSubMenus = item.subMenus || [];
      }

      return {
        key: isReportsMenu
          ? '/reports/'
          : item.subMenus
            ? item.id
            : item.listUrl || item.createUrl || '#',
        icon: iconMapping[item?.icon] || <span className="anticon" />,
        id: item.id,
        label: item.name,
        children: isReportsMenu ? null : item.subMenus?.map(transformItem),
      };
    };

    // Transform the original menu items
    const transformedItems = menuData?.map(transformItem);

    // Insert divider after each menu item (except the last)
    const itemsWithDividers = [];
    transformedItems.forEach((item, index) => {
      itemsWithDividers.push(item);
      if (index <= transformedItems.length - 1) {
        itemsWithDividers.push({
          type: 'divider',
        });
      }
    });

    localStorage.setItem('reportsSubMenus', encryptData(reportsSubMenus));
    return itemsWithDividers;
  };

  useEffect(() => {
    const fetchAndSetMenuData = () => {
      const menuData = collapsed
        ? transformMenuData2(data)
        : transformMenuData(data);
      setNavigationMenus(menuData);
    };
    fetchAndSetMenuData();
  }, [collapsed]);

  useEffect(() => {
    /**
     * Finds the title of the current page based on the URL.
     *
     * @param {Array} menus - The navigation menus.
     * @param {string} key - The current URL.
     * @returns {string|undefined} The title of the current page.
     */
    const findTitle = (menus, key) => {
      for (const menu of menus) {
        if (menu.key === key) {
          return menu.label;
        }
        if (menu.children) {
          const childTitle = findTitle(menu.children, key);
          if (childTitle) {
            return childTitle;
          }
        }
      }
      return;
    };
    const currentKey = location.pathname;
    const title = findTitle(navigationMenus, currentKey);
    setPageTitle(title || 'Dashboard');
  }, [location.pathname]);

  /**
   * Shows the drawer.
   */
  const showDrawer = () => {
    setDrawerOpen(true);
  };

  /**
   * Closes the drawer.
   */
  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  /**
   * Handles the change of open submenu keys.
   *
   * @param {Array} keys - The open keys.
   */

  //pervious logic

  const handleHoverOpenChange = (keys) => {
    const rootSubmenuKeys = navigationMenus.map((menu) => menu.key);
    const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);

    if (rootSubmenuKeys.indexOf(latestOpenKey) === -1) {
      setOpenKeys(keys);
    } else {
      setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    }
  };

  const onOpenChange = (keys) => {
    const rootSubmenuKeys = navigationMenus.map((menu) => menu.key);
    const latestOpenKey = keys.find((key) => !openKeys.includes(key));

    if (!latestOpenKey) {
      setOpenKeys([]);
      return;
    }

    if (rootSubmenuKeys.includes(latestOpenKey)) {
      // If a root menu is opened, close all and keep only it
      setOpenKeys([latestOpenKey]);
    } else {
      // Find the root key for the latest opened nested submenu
      const rootForLatest = rootSubmenuKeys.find((rootKey) =>
        keys.includes(rootKey),
      );

      // Keep the root and the latest nested submenu only
      setOpenKeys(
        rootForLatest ? [rootForLatest, latestOpenKey] : [latestOpenKey],
      );
    }
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Sider
        className="custom-sider"
        width={235}
        trigger={null}
        collapsible
        collapsed={collapsed}
        style={{
          backgroundColor: 'rgb(237, 242, 248)',
          ...siderStyle,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // marginBottom: '20px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // marginBottom: '20px',
              height: 'auto',
              width: '100%',
            }}
          >
            <img
              src={logo}
              alt=""
              onClick={() => navigate('/dashboard/')}
              style={{
                marginTop: '20px',
                height: '100%',
                width: '90%',
                objectFit: 'cover',
                cursor: 'pointer',
                // borderRadius: '50%',
              }}
            />
          </div>
        </div>
        {/* <div
          style={{
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '25px',
            cursor: 'pointer',
            gap: '5px',
            zIndex: '1000',
            color: '#636972',
            fontWeight: '500',
            marginBottom: '5px',
          }}
          onClick={showDrawer}
        >
          <PlusCircleFilled
            style={{
              color: 'green',
              fontSize: '25px',
            }}
          />
          {!collapsed && <p className="username">Create new</p>}
        </div> */}

        {/* {!collapsed && (
          <div className="menu-search-box" style={{ marginTop: '1%' }}>
            <Search
              className="menu-search-box"
              placeholder="search menu..."
              onSearch={handleSearch}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
        )} */}

        {searchResults.length > 0 && (
          <Menu
            mode="inline"
            onClick={(e) => toNavigate(e.key)}
            items={searchResults?.map((result) => ({
              key: result.key,
              label: result.label,
            }))}
          />
        )}
        <ConfigProvider
          theme={{
            components: {
              Menu: {
                itemHeight: '32px',
                itemMarginBlock: '2px',
              },
            },
          }}
        >
          {searchResults.length === 0 && (
            <Menu
              mode="inline"
              // items={navigationMenus}
              items={searchResults.length ? searchResults : navigationMenus}
              selectedKeys={[currentUrl]}
              openKeys={openKeys}
              // onOpenChange={onOpenChange}
              onOpenChange={collapsed ? handleHoverOpenChange : onOpenChange}
              onClick={(e) => navigate(e.key)}
              style={{ background: 'rgb(237, 242, 248)', color: '#343c46' }}
            />
          )}
        </ConfigProvider>
        <SideNavDrawer
          title="Basic Drawer"
          placement="top"
          closable={false}
          onClose={closeDrawer}
          open={drawerOpen}
        />
      </Sider>
    </>
  );
};

SideBar.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  colorBgContainer: PropTypes.string,
};

export default SideBar;
